import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Input, Select, Tag, Space, Modal,
  notification, Row, Col, Statistic, Descriptions, Avatar, Badge,
} from 'antd';
import {
  SearchOutlined, EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ReloadOutlined, HeartOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Option } = Select;
const { TextArea } = Input;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const STATUS_TAG = {
  pending:  <Tag color="orange">⏳ Pending Approval</Tag>,
  active:   <Tag color="green">✅ Active</Tag>,
  paused:   <Tag color="blue">⏸ Paused</Tag>,
  married:  <Tag color="purple">💍 Married</Tag>,
  removed:  <Tag color="red">❌ Removed</Tag>,
};

const GENDER_COLOR = { Male: '#7a0000', Female: '#c026d3' };

export default function VivahList() {
  const [data,       setData]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [stats,      setStats]      = useState({});
  const [viewItem,   setViewItem]   = useState(null);
  const [noteModal,  setNoteModal]  = useState(null);
  const [adminNote,  setAdminNote]  = useState('');
  const [saving,     setSaving]     = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/admin/vivah/stats`, { headers: auth() });
      if (r.data.status) setStats(r.data.result || {});
    } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: 30,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      });
      const r = await axios.get(`${baseUrl}/admin/vivah?${params}`, { headers: auth() });
      if (r.data.status) {
        setData(r.data.result || []);
        setTotal(r.data.total || 0);
      }
    } catch {
      notification.error({ message: 'Failed to load profiles', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  const handleApprove = async (id) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${baseUrl}/admin/vivah/${id}`, { is_approved: 1 }, { headers: auth() });
      if (r.data.status) {
        notification.success({ message: 'Profile Approved ✅', placement: 'topRight' });
        fetchData(); fetchStats();
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    if (!adminNote.trim()) {
      notification.warning({ message: 'Please enter a reason for removal', placement: 'topRight' });
      return;
    }
    setSaving(true);
    try {
      const r = await axios.patch(`${baseUrl}/admin/vivah/${noteModal}`, { status: 'removed', admin_note: adminNote }, { headers: auth() });
      if (r.data.status) {
        notification.success({ message: 'Profile Removed', placement: 'topRight' });
        setNoteModal(null); setAdminNote('');
        fetchData(); fetchStats();
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    {
      title: 'Profile',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar icon={<UserOutlined />} style={{ background: GENDER_COLOR[r.gender] || '#6b7280', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{r.full_name}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {r.age ? `${r.age} yrs` : ''}{r.height ? ` · ${r.height}` : ''}
            </div>
            <Tag style={{ marginTop: 2 }} color={GENDER_COLOR[r.gender] || '#6b7280'}>{r.gender}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Education & Career',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{r.education || '—'}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{r.occupation || ''}{r.income_range ? ` · ${r.income_range}` : ''}</div>
        </div>
      ),
    },
    {
      title: 'Community',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{r.gotra ? `Gotra: ${r.gotra}` : '—'}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{r.city || ''}{r.is_nri ? ' · NRI' : ''}</div>
        </div>
      ),
    },
    {
      title: 'Posted By',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{r.posted_by_name || '—'}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{r.posted_by_phone || ''}</div>
        </div>
      ),
    },
    { title: 'Status', dataIndex: 'status', width: 140, render: v => STATUS_TAG[v] || <Tag>{v}</Tag> },
    {
      title: 'Actions',
      width: 160,
      render: (_, r) => (
        <Space wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setViewItem(r)}>View</Button>
          {r.status === 'pending' && (
            <Button
              size="small" type="primary" icon={<CheckCircleOutlined />}
              onClick={() => handleApprove(r.id)}
              style={{ background: '#16a34a', borderColor: '#16a34a' }}
            >
              Approve
            </Button>
          )}
          {r.status !== 'removed' && (
            <Button
              size="small" danger icon={<CloseCircleOutlined />}
              onClick={() => { setNoteModal(r.id); setAdminNote(''); }}
            >
              Remove
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      {/* Stats Row */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { label: 'Total',    value: stats.total   || 0, color: '#7a0000' },
          { label: 'Pending',  value: stats.pending  || 0, color: '#d97706' },
          { label: 'Active',   value: stats.active   || 0, color: '#16a34a' },
          { label: 'Married',  value: stats.married  || 0, color: '#7c3aed' },
          { label: 'Removed',  value: stats.removed  || 0, color: '#dc2626' },
        ].map(s => (
          <Col key={s.label} xs={12} sm={8} md={4}>
            <Card size="small" style={{ borderLeft: `4px solid ${s.color}`, borderRadius: 10 }} bodyStyle={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{s.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title={<><HeartOutlined style={{ color: '#c026d3', marginRight: 8 }} />Vivah Parichay — Profile Management</>}
        extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchData(); fetchStats(); }}>Refresh</Button>}
      >
        <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search name, gotra, city, phone..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Status"
              value={status || undefined}
              onChange={v => { setStatus(v || ''); setPage(1); }}
              allowClear
            >
              <Option value="pending">Pending</Option>
              <Option value="active">Active</Option>
              <Option value="paused">Paused</Option>
              <Option value="married">Married</Option>
              <Option value="removed">Removed</Option>
            </Select>
          </Col>
        </Row>

        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize: 30,
            total,
            onChange: setPage,
            showTotal: t => `Total ${t} profiles`,
          }}
        />
      </Card>

      {/* View Profile Modal */}
      <Modal
        open={!!viewItem}
        title={viewItem?.full_name}
        onCancel={() => setViewItem(null)}
        footer={[
          viewItem?.status === 'pending' && (
            <Button
              key="approve" type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => { handleApprove(viewItem.id); setViewItem(null); }}
              style={{ background: '#16a34a', borderColor: '#16a34a' }}
            >
              Approve
            </Button>
          ),
          viewItem?.status !== 'removed' && (
            <Button
              key="remove" danger icon={<CloseCircleOutlined />}
              onClick={() => { setNoteModal(viewItem.id); setViewItem(null); setAdminNote(''); }}
            >
              Remove
            </Button>
          ),
          <Button key="close" onClick={() => setViewItem(null)}>Close</Button>,
        ].filter(Boolean)}
        width={620}
      >
        {viewItem && (
          <Descriptions column={2} size="small" bordered style={{ marginTop: 8 }}>
            <Descriptions.Item label="Full Name" span={2}>{viewItem.full_name}</Descriptions.Item>
            <Descriptions.Item label="Gender">{viewItem.gender}</Descriptions.Item>
            <Descriptions.Item label="Age">{viewItem.age ? `${viewItem.age} yrs` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Height">{viewItem.height || '—'}</Descriptions.Item>
            <Descriptions.Item label="Date of Birth">{viewItem.dob?.slice(0, 10) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Education">{viewItem.education || '—'}</Descriptions.Item>
            <Descriptions.Item label="Occupation">{viewItem.occupation || '—'}</Descriptions.Item>
            <Descriptions.Item label="Income Range">{viewItem.income_range || '—'}</Descriptions.Item>
            <Descriptions.Item label="NRI">{viewItem.is_nri ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="City">{viewItem.city || '—'}</Descriptions.Item>
            <Descriptions.Item label="State">{viewItem.state || '—'}</Descriptions.Item>
            <Descriptions.Item label="Gotra">{viewItem.gotra || '—'}</Descriptions.Item>
            <Descriptions.Item label="Kuldevi">{viewItem.kuldevi || '—'}</Descriptions.Item>
            <Descriptions.Item label="Village">{viewItem.village || '—'}</Descriptions.Item>
            <Descriptions.Item label="Father's Name">{viewItem.father_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Family Type">{viewItem.family_type || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status" span={2}>{STATUS_TAG[viewItem.status] || viewItem.status}</Descriptions.Item>
            <Descriptions.Item label="Posted By">{viewItem.posted_by_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{viewItem.posted_by_phone || '—'}</Descriptions.Item>
            {viewItem.admin_note && (
              <Descriptions.Item label="Admin Note" span={2}>{viewItem.admin_note}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        open={!!noteModal}
        title="Remove Profile"
        onCancel={() => { setNoteModal(null); setAdminNote(''); }}
        footer={null}
        width={420}
      >
        <div>
          <p style={{ color: '#dc2626', marginBottom: 12 }}>
            Please enter a reason for removing this profile. The member will be notified.
          </p>
          <TextArea
            rows={3}
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            placeholder="Reason for removal (required)..."
            style={{ marginBottom: 14 }}
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setNoteModal(null); setAdminNote(''); }}>Cancel</Button>
            <Button danger loading={saving} icon={<CloseCircleOutlined />} onClick={handleRemove}>
              Remove Profile
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
